// Claude: extracted from Buyer.tsx — state, handlers, effects 20-Apr-2026
// Claude: handleSubmit auto-opens BuyerRenewModal when plan changed; handleConfirmRenew updated payload 25-Apr-2026
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment-timezone";
import { Modal } from "antd";
import { AdminBuyer } from "../../Interface/AdminInterfaces";
import { AdminAPI } from "../../Apis/adminAPI";
import { AdminBuyerAPI } from "../../Apis/adminBuyerAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { validateEmail } from "../../Utils/Validator";
import { runVerificationCheck } from "../../Utils/verificationCheck";
import { errorHandler } from "../../Utils/handler";
import { RenewData } from "./BuyerModals/BuyerRenewModal";

export const PAGESIZE = 20;
const mandatoryFields = ["buyerCompanyName", "buyerPersonName", "buyerOfficialEmail"];

export const emptyBuyerSearch = {
  buyerCompanyName: '', buyerPersonName: '', buyerPersonCity: '', buyerPersonState: '',
  buyerPersonCountry: '', buyerOfficialEmail: '', buyersocialEmail: '',
  buyerPhoneNumber: '', buyerDesignation: '', buyerTimezone: '',
  buyerCompanyWebsite: '', buyerCompanyInfo: '',
  status: '', verificationStatus: '', linkedinVerified: '', industryId: '',
  subscriptionPlanId: '', buyerCompanyType: '',
};

const emptyForm: AdminBuyer = {
  eventId: "", buyerCompanyName: "", buyerPersonName: "",
  buyerPersonCity: "", buyerPersonState: "", buyerPersonCountry: "",
  buyerOfficialEmail: "", buyersocialEmail: "", buyerPhoneNumber: "",
  buyerDesignation: "", buyerTimezone: "", buyerCompanyWebsite: "",
  buyerCompanyInfo: "", industryIds: [], subscriptionPlanId: "",
  buyerCompanyType: [], buyerCompanyTypeOther: "", adminNotes: "",
};

export const DEFAULT_BUYER_COLS = [
  "buyerCompanyName", "buyerPersonCountry", "buyerPersonName",
  "buyerOfficialEmail", "buyerPhoneNumber", "subscriptionPlanId",
  "verificationStatus", "actions",
];

export const useBuyerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Claude: skip the initial empty fetch when returning from edit — restore effect will update appliedSearch and re-trigger the fetch 28-04-2026
  const skipInitialFetchRef = useRef(!!(location.state as any)?.restoredSearch);
  const { error, success } = useToastWrapper();

  const [loading, setLoading] = useState(false);
  const [countryOptions, setCountryOptions] = useState<{ value: string }[]>([]);
  const [buyersList, setBuyersList] = useState<any[]>([]);
  const [totalBuyersCount, setTotalBuyersCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const showModal = () => setIsModalVisible(true);
  const handleOk = () => setIsModalVisible(false);

  const [isBulkModalVisible, setIsBulkModalVisible] = useState(false);
  const [isBulkConfirmModalVisible, setIsBulkConfirmModalVisible] = useState(false);
  const [bulkUploadDone, setBulkUploadDone] = useState(false);
  const [bulkDocument, setBulkDocument] = useState<any>("");
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [bulkEventId, setBulkEventId] = useState("");
  const showBulkModal = () => { setBulkDocument(""); setBulkEventId(""); setFileInputKey(Date.now()); setBulkUploadDone(false); setIsBulkModalVisible(true); };
  const showBulkConfirmModal = () => setIsBulkConfirmModalVisible(true);
  const hideBulkConfirmModal = () => { setIsBulkConfirmModalVisible(false); setBulkUploadDone(false); };
  const handleBulkOk = () => setIsBulkModalVisible(false);

  const [eventData, setEventData] = useState<any[]>([]);
  const [sortEventId, setSortEventId] = useState("");
  const [pendingEventId, setPendingEventId] = useState("");
  const timezones = moment.tz.names();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const showDeleteModal = () => setIsDeleteModalOpen(true);
  const handleDeleteModalOk = () => setIsDeleteModalOpen(false);

  const [formData, setFormData] = useState<AdminBuyer>({ ...emptyForm });
  const [buyerPlansData, setBuyerPlansData] = useState<any[]>([]);
  const [edit, setEdit] = useState(false);
  const [editId, setEditId] = useState("");
  const [editInitialPlanId, setEditInitialPlanId] = useState("");
  const [editInitialIsPlanActive, setEditInitialIsPlanActive] = useState(false);
  const [buyerEventIds, setBuyerEventIds] = useState<string[]>([]);

  const [industryNodes, setTaxonomyNodes] = useState<any[]>([]);
  const [isIndustryModalVisible, setIsIndustryModalVisible] = useState(false);
  const [assignIndustryIds, setAssignIndustryIds] = useState<string[]>([]);

  const [appliedSearch, setAppliedSearch] = useState({ ...emptyBuyerSearch });
  const [pendingSearch, setPendingSearch] = useState({ ...emptyBuyerSearch });
  const [pendingSearchModes, setPendingSearchModes] = useState<Record<string, string>>({});

  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [overrideBuyerId, setOverrideBuyerId] = useState("");
  const [overrideStatus, setOverrideStatus] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideNotes, setOverrideNotes] = useState<{ reason: string; status: string; timestamp: string }[]>([]);
  const [domainConflictEmail, setDomainConflictEmail] = useState<string | null>(null);
  const [overrideCurrentStatus, setOverrideCurrentStatus] = useState("");

  const [selectAllDB, setSelectAllDB] = useState(false);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);

  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [renewTargetBuyer, setRenewTargetBuyer] = useState<any>(null);
  const [renewLoading, setRenewLoading] = useState(false);

  const [visibleColKeys, setVisibleColKeys] = useState<string[]>(() => {
    try { const s = localStorage.getItem("adminBuyerColumns"); return s ? JSON.parse(s) : DEFAULT_BUYER_COLS; }
    catch { return DEFAULT_BUYER_COLS; }
  });
  const [colPopoverOpen, setColPopoverOpen] = useState(false);
  const toggleCol = (key: string) => {
    const next = visibleColKeys.includes(key) ? visibleColKeys.filter(k => k !== key) : [...visibleColKeys, key];
    setVisibleColKeys(next);
    localStorage.setItem("adminBuyerColumns", JSON.stringify(next));
  };

  // ── Data fetching ────────────────────────────────────────────────────────────

  const getEventsData = async () => {
    setLoading(true);
    try {
      const res = await AdminAPI.getEvent();
      if (res?.data?.data?.length > 0)
        setEventData(res.data.data.map((e: any) => ({ label: e.eventName, value: e._id })));
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); }
  };

  const getBuyersData = async () => {
    setLoading(true);
    try {
      const params: any = { eventId: sortEventId, page: currentPage, limit: PAGESIZE };
      Object.entries(appliedSearch).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await AdminAPI.getEventBuyer(params);
      if (res) { setBuyersList(res.data.data); setTotalBuyersCount(res.data.totalCount); }
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); }
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const clearData = () => {
    setBuyerEventIds([]);
    setEditInitialPlanId(""); setEditInitialIsPlanActive(false);
    setFormData({ ...emptyForm });
  };

  const handleChange = (e: any) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const validate = () => {
    if (mandatoryFields.some(f => !formData[f as keyof AdminBuyer])) { error("Please fill all the mandatory fields", ""); return false; }
    if (!validateEmail(formData.buyerOfficialEmail)) { error("Please enter valid email", ""); return false; }
    if (formData.buyersocialEmail && !validateEmail(formData.buyersocialEmail)) { error("Please enter valid email", ""); return false; }
    return true;
  };

  const handleDelete = async (key: any) => AdminAPI.deleteBuyer(key);

  const handleDeleteOk = async (rows: any[]) => {
    setLoading(true);
    try {
      // Claude: bulk endpoint to avoid N parallel requests 24-04-2026
      const toDelete = selectAllDB ? rows.filter((item: any) => !excludedIds.includes(item._id)) : rows;
      const ids = toDelete.map((item: any) => item.buyerId || item._id);
      await AdminAPI.bulkDeleteBuyers(ids);
      success("Buyers deleted successfully", "");
      getBuyersData();
    } catch { error("Buyers delete failed"); }
    finally { setSelectedRowKeys([]); setSelectedRows([]); setSelectAllDB(false); setExcludedIds([]); setLoading(false); }
  };

  const handleUnlinkOk = async (rows: any[]) => {
    setLoading(true);
    try {
      // Claude: in selectAllDB mode, skip explicitly excluded rows 24-04-2026
      const toUnlink = selectAllDB ? rows.filter((item: any) => !excludedIds.includes(item._id)) : rows;
      await Promise.all(toUnlink.map((item: any) => AdminAPI.unlinkBuyerFromEvent(item.buyerId || item._id, sortEventId)));
      success("Buyers unlinked from event successfully", "");
      getBuyersData();
    } catch { error("Unlink failed"); }
    finally { setSelectedRowKeys([]); setSelectedRows([]); setSelectAllDB(false); setExcludedIds([]); setLoading(false); }
  };

  const resetPasswordClick = async (rows: any[]) => {
    if (rows.length === 0) { error("Please select atleast one entry to reset !"); return; }
    setLoading(true);
    try {
      await Promise.all(rows.map((item: any) => AdminAPI.resetPassword(item.userId)));
      success("Password reset successfully", "");
    } catch { error("Password reset failed"); }
    finally { setLoading(false); setSelectedRowKeys([]); setSelectedRows([]); }
  };

  const handleAssignIndustry = async () => {
    if (assignIndustryIds.length === 0) { error("Please select at least one industry"); return; }
    if (selectedRows.length === 0) { error("Please select at least one buyer"); return; }
    const buyerIds = selectedRows.map((r: any) => r.buyerId);
    try {
      await Promise.all(assignIndustryIds.map((industryId) => AdminAPI.mapBuyersToIndustry({ industryId, buyerIds })));
      success("Industries assigned successfully");
      setIsIndustryModalVisible(false);
      setAssignIndustryIds([]);
      getBuyersData();
    } catch (err) { error(errorHandler(err)); }
  };

  const handleOverrideVerification = async () => {
    if (!overrideStatus) { error("Please select a status"); return; }
    if (!overrideReason.trim()) { error("Please provide a reason"); return; }
    setLoading(true);
    try {
      await AdminAPI.overrideBuyerVerification(overrideBuyerId, overrideStatus, overrideReason);
      success("Verification status updated");
      setIsOverrideModalOpen(false);
      setOverrideReason(""); setOverrideStatus("");
      getBuyersData();
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload: any = { ...formData };
    if (!payload.eventId) delete payload.eventId;
    if (edit) payload.eventIds = buyerEventIds;
    if (edit) {
      const planChanged = !!formData.subscriptionPlanId && formData.subscriptionPlanId !== editInitialPlanId;
      const savedBuyerId = editId;
      const savedSubscriptionPlanId = formData.subscriptionPlanId;
      setLoading(true);
      try {
        const res = await AdminAPI.putBuyer(editId, payload);
        success("Buyer edited successfully", "");
        clearData(); getBuyersData(); handleOk();
        if (res?.data?.verificationTriggered) {
          setOverrideBuyerId(res.data.buyerId);
          setOverrideStatus(""); setOverrideReason(""); setOverrideNotes([]);
          setIsOverrideModalOpen(true);
        }
        // Claude: auto-open Subscription modal when admin assigns a new plan 25-Apr-2026
        if (planChanged) {
          setRenewTargetBuyer({ buyerId: savedBuyerId, subscriptionPlanId: savedSubscriptionPlanId });
          setIsRenewModalOpen(true);
        }
      } catch (err) { error(errorHandler(err)); }
      finally { setLoading(false); }
    } else {
      const verifResult = runVerificationCheck(formData.buyerOfficialEmail, formData.buyerCompanyWebsite);
      const doCreate = async () => {
        setLoading(true);
        try {
          await AdminAPI.postBuyer(payload);
          success("Buyer added successfully", "");
          clearData(); getBuyersData(); handleOk();
        } catch (err) { error(errorHandler(err)); }
        finally { setLoading(false); }
      };
      if (verifResult.status === "unverified") {
        Modal.confirm({ title: "Verification Warning", content: `This buyer will be created as unverified. Reason: ${verifResult.reason}. Continue?`, okText: "Continue", cancelText: "Cancel", onOk: doCreate });
        return;
      }
      await doCreate();
    }
  };

  const handleBulkUpload = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", bulkDocument);
      const res = await AdminAPI.bulkUploadUsers(fd, { role: "Buyer", eventId: bulkEventId });
      if (res) { clearData(); getBuyersData(); handleBulkOk(); setBulkUploadDone(true); }
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); }
  };

  const onSelectChange = (keys: any, rows: any) => {
    if (selectAllDB) {
      // Claude: in selectAllDB mode, track exclusions instead of clearing selectAllDB 24-04-2026
      const currentPageIds = buyersList.map((r: any) => r._id);
      const deselected = currentPageIds.filter((id: string) => !(keys as string[]).includes(id));
      const reselected = currentPageIds.filter((id: string) => (keys as string[]).includes(id));
      setExcludedIds(prev => [...prev.filter(id => !reselected.includes(id)), ...deselected.filter(id => !prev.includes(id))]);
      const otherPageKeys = (selectedRowKeys as string[]).filter((id: string) => !currentPageIds.includes(id));
      setSelectedRowKeys([...otherPageKeys, ...keys]);
      return;
    }
    setSelectedRowKeys(keys); setSelectedRows(rows); setSelectAllDB(false);
  };

  const handleSelectAllFromDB = async () => {
    setLoading(true);
    try {
      const params: any = { eventId: sortEventId, limit: totalBuyersCount, page: 1 };
      Object.entries(appliedSearch).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await AdminAPI.getEventBuyer(params);
      if (res) { const all = res.data.data; setSelectedRows(all); setSelectedRowKeys(all.map((r: any) => r._id)); setSelectAllDB(true); }
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); }
  };

  const getSearchState = () => ({ appliedSearch, pendingSearch, pendingSearchModes, currentPage, sortEventId, pendingEventId });

  // Column action callbacks (passed to getBuyerTableConfig)
  // Claude: navigate to edit page — replaced modal 25-Apr-2026
  const handleEditClick = (object: any) => {
    navigate(`/admin/buyers/${object.buyerId}/edit`, {
      state: {
        formData: {
          eventId: object.eventId, buyerCompanyName: object.buyerCompanyName,
          buyerPersonName: object.buyerPersonName, buyerPersonCity: object.buyerPersonCity,
          buyerPersonState: object.buyerPersonState, buyerPersonCountry: object.buyerPersonCountry,
          buyerOfficialEmail: object.buyerOfficialEmail, buyersocialEmail: object.buyersocialEmail,
          buyerPhoneNumber: object.buyerPhoneNumber, buyerDesignation: object.buyerDesignation,
          buyerTimezone: object.buyerTimezone, buyerCompanyWebsite: object.buyerCompanyWebsite,
          buyerCompanyInfo: object.buyerCompanyInfo, industryIds: object.industryIds?.map(String) ?? [],
          subscriptionPlanId: object.subscriptionPlanId ?? "",
          buyerCompanyType: object.buyerCompanyType ?? [], buyerCompanyTypeOther: object.buyerCompanyTypeOther ?? "",
          adminNotes: object.adminNotes ?? "",
        },
        searchState: getSearchState(),
      },
    });
  };

  const handleRewardsClick = (object: any) => navigate('/admin/buyer-rewards', { state: { buyerId: object.buyerId, buyerobject: object } });

  // Claude: open renew modal — plan change + payment + credits captured in one flow 21-04-2026
  const handleRenewClick = (object: any) => {
    setRenewTargetBuyer(object);
    setIsRenewModalOpen(true);
  };

  const handleConfirmRenew = async (data: RenewData) => {
    if (!renewTargetBuyer) return;
    setRenewLoading(true);
    try {
      await AdminBuyerAPI.renewBuyerSubscription(
        renewTargetBuyer.buyerId,
        data.subscriptionPlanId ? { subscriptionPlanId: data.subscriptionPlanId } : {},
      );
      // Claude: new payload — planAmount, creditsUtilized, paidAmount 25-Apr-2026
      if (data.date && data.paymentReference) {
        await AdminBuyerAPI.addBuyerPaymentRecord(renewTargetBuyer.buyerId, {
          date: data.date,
          paymentReference: data.paymentReference,
          planName: data.planName ?? "",
          planAmount: parseFloat(data.planAmount ?? "0") || 0,
          creditsUtilized: parseFloat(data.creditsUtilized ?? "0") || 0,
          paidAmount: parseFloat(data.paidAmount ?? "0") || 0,
          rfxAdded: buyerPlansData.find((p: any) => p._id === data.subscriptionPlanId)?.rfxLimit ?? 0, // Claude: auto-derived from plan 25-Apr-2026
        });
      }
      if (data.creditsUtilized && parseFloat(data.creditsUtilized) > 0) {
        await AdminBuyerAPI.adjustBuyerCredits(renewTargetBuyer.buyerId, { amountUsed: parseFloat(data.creditsUtilized) });
      }
      success("Subscription renewed successfully", "");
      setIsRenewModalOpen(false);
      setRenewTargetBuyer(null);
      getBuyersData();
    } catch (err) { error(errorHandler(err)); }
    finally { setRenewLoading(false); }
  };

  const handleOverrideClick = (object: any) => {
    setOverrideBuyerId(object.buyerId);
    setOverrideStatus(""); setOverrideReason(""); setDomainConflictEmail(null);
    setOverrideNotes(object.verificationNotes || []);
    setOverrideCurrentStatus(object.verificationStatus || "");
    setIsOverrideModalOpen(true);
  };

  // Claude: navigate to add page — replaced modal 25-Apr-2026
  const handleAddClick = () => {
    navigate('/admin/buyers/new', { state: { searchState: getSearchState() } });
  };

  const clearSelection = () => { setSelectedRowKeys([]); setSelectedRows([]); setSelectAllDB(false); setExcludedIds([]); };

  const handleExportCsv = (sortEventIdVal: string, search: typeof emptyBuyerSearch) => {
    const params: any = { eventId: sortEventIdVal };
    Object.entries(search).forEach(([k, v]) => { if (v) params[k] = v; });
    AdminAPI.exportBuyers(params);
  };

  // ── Effects ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    getEventsData();
    AdminAPI.getIndustryTree().then((nodes: any) => setTaxonomyNodes(Array.isArray(nodes) ? nodes : (nodes?.data ?? []))).catch(() => {});
    AdminAPI.getBuyerSubscriptions().then((res: any) => setBuyerPlansData(res?.data?.data?.subscriptions ?? [])).catch(() => {});
    // Claude: restore advanced search state when returning from edit page 25-Apr-2026
    const restored = (location.state as any)?.restoredSearch;
    if (restored) {
      setAppliedSearch(restored.appliedSearch ?? { ...emptyBuyerSearch });
      setPendingSearch(restored.pendingSearch ?? { ...emptyBuyerSearch });
      setPendingSearchModes(restored.pendingSearchModes ?? {});
      setCurrentPage(restored.currentPage ?? 1);
      setSortEventId(restored.sortEventId ?? "");
      setPendingEventId(restored.pendingEventId ?? "");
    }
  }, []);

  useEffect(() => { if (skipInitialFetchRef.current) { skipInitialFetchRef.current = false; return; } getBuyersData(); setSelectedRowKeys([]); setSelectedRows([]); setSelectAllDB(false); setExcludedIds([]); }, [sortEventId, currentPage, appliedSearch]);

  useEffect(() => {
    if (isModalVisible) requestAnimationFrame(() => document.querySelectorAll('.ant-modal-body').forEach((el) => { (el as HTMLElement).scrollTop = 0; }));
  }, [isModalVisible]);

  const rowSelection = { selectedRowKeys, onChange: onSelectChange };
  const generateRowKey = (record: any) => record._id;

  return {
    loading, buyersList, totalBuyersCount,
    currentPage, setCurrentPage, selectedRowKeys, selectedRows, selectAllDB, excludedIds,
    isBulkModalVisible, handleBulkOk, isBulkConfirmModalVisible, bulkUploadDone,
    bulkDocument, setBulkDocument, fileInputKey, bulkEventId,
    showBulkModal, showBulkConfirmModal, hideBulkConfirmModal,
    eventData, sortEventId, setSortEventId, pendingEventId, setPendingEventId,
    isDeleteModalOpen, handleDeleteModalOk, showDeleteModal, handleDeleteOk, handleUnlinkOk,
    buyerPlansData,
    industryNodes, isIndustryModalVisible, setIsIndustryModalVisible,
    assignIndustryIds, setAssignIndustryIds, handleAssignIndustry,
    appliedSearch, setAppliedSearch,
    pendingSearch, setPendingSearch, pendingSearchModes, setPendingSearchModes,
    isOverrideModalOpen, setIsOverrideModalOpen, overrideBuyerId,
    overrideStatus, setOverrideStatus, overrideReason, setOverrideReason,
    overrideNotes, domainConflictEmail, setDomainConflictEmail, overrideCurrentStatus,
    visibleColKeys, colPopoverOpen, setColPopoverOpen, toggleCol,
    handleBulkUpload, handleOverrideVerification,
    resetPasswordClick, handleSelectAllFromDB, getBuyersData,
    handleEditClick, handleRewardsClick, handleRenewClick, handleOverrideClick,
    handleAddClick, clearSelection, handleExportCsv,
    isRenewModalOpen, setIsRenewModalOpen, renewTargetBuyer, renewLoading, handleConfirmRenew,
    rowSelection, generateRowKey, error,
  };
};
